/**
 * EingangskorbPage — Universal-Eingangskorb (Issue #643)
 *
 * Zwei-Tab-Layout:
 *   Tab 1 „Alle Eingänge"      — inbox_items mit Filter + Drag&Drop-Upload
 *   Tab 2 „Buchhaltungs-Belege" — bisherige eingangsbelege-Ansicht
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useEingangsbelege, createEingangsbeleg, updateEingangsbeleg } from '@/hooks/useBuchhaltung';
import { useInboxItems, uploadInboxItem, sortInboxItem, ablehnenInboxItem } from '@/hooks/useInboxItems';
import { filterInboxItems, validateUploadFile, ZIELMODUL_LABELS } from '@/lib/inbox-items';
import { useAuth } from '@/contexts/AuthContext';
import type {
  Eingangsbeleg,
  EingangsbelegStatus,
  EingangsbelegTyp,
  FirmaId,
  InboxItem,
  InboxItemStatus,
  InboxQuelle,
  InboxKlassifizierung,
} from '@/lib/database.types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Inbox, Receipt, CheckCircle2, Eye,
  Plus, Search, X, Loader2, Mail, Euro,
  Upload, ScanLine, RefreshCw, MessageSquare, Printer, PenLine,
  FileText, AlertCircle, ArrowRight, Ban,
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================
// SHARED HELPERS
// ============================================================

function formatEuro(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ============================================================
// BUCHHALTUNGS-BELEGE TAB (bisherige Ansicht)
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  neu: { label: 'Neu', color: 'bg-blue-50 text-blue-600' },
  geprueft: { label: 'Geprüft', color: 'bg-amber-50 text-amber-600' },
  gebucht: { label: 'Gebucht', color: 'bg-purple-50 text-purple-600' },
  freigegeben: { label: 'Freigegeben', color: 'bg-emerald-50 text-emerald-600' },
  ueberwiesen: { label: 'Überwiesen', color: 'bg-gray-100 text-gray-500' },
  abgelehnt: { label: 'Abgelehnt', color: 'bg-red-50 text-red-600' },
};

const TYP_LABELS: Record<string, string> = {
  eingangsrechnung: 'Eingangsrechnung',
  quittung: 'Quittung',
  gutschrift: 'Gutschrift',
  sonstiges: 'Sonstiges',
};

const FIRMA_LABELS: Record<string, string> = {
  gutachter_koop: 'Gutachter Koop',
  lcs_gmbh: 'LCS GmbH',
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

function KPICard({ label, value, subtext, icon: Icon, color }: {
  label: string; value: string; subtext?: string; icon: typeof Receipt; color: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 tracking-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {subtext && <div className="text-[10px] text-gray-400 mt-1">{subtext}</div>}
    </div>
  );
}

function ErfassenDialog({ open, onClose, onSave }: {
  open: boolean;
  onClose: () => void;
  onSave: (beleg: Partial<Eingangsbeleg>) => void;
}) {
  const [firma, setFirma] = useState<FirmaId>('gutachter_koop');
  const [typ, setTyp] = useState<EingangsbelegTyp>('eingangsrechnung');
  const [absenderFirma, setAbsenderFirma] = useState('');
  const [absenderName, setAbsenderName] = useState('');
  const [netto, setNetto] = useState('');
  const [mwstSatz, setMwstSatz] = useState('19');
  const [belegdatum, setBelegdatum] = useState('');
  const [faelligAm, setFaelligAm] = useState('');
  const [verwendungszweck, setVerwendungszweck] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const nettoNum = parseFloat(netto) || 0;
  const mwstNum = parseFloat(mwstSatz) || 0;
  const mwstBetrag = nettoNum * (mwstNum / 100);
  const brutto = nettoNum + mwstBetrag;

  const handleSave = async () => {
    if (!absenderFirma && !absenderName) {
      toast.error('Bitte Absender angeben');
      return;
    }
    if (nettoNum <= 0) {
      toast.error('Bitte Nettobetrag angeben');
      return;
    }
    setSaving(true);
    try {
      onSave({
        firma,
        typ,
        absender_firma: absenderFirma || null,
        absender_name: absenderName || null,
        netto_betrag: nettoNum,
        mwst_satz: mwstNum,
        mwst_betrag: mwstBetrag,
        brutto_betrag: brutto,
        belegdatum: belegdatum || null,
        faellig_am: faelligAm || null,
        verwendungszweck: verwendungszweck || null,
        status: 'neu' as EingangsbelegStatus,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Beleg erfassen</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Firma</label>
            <select value={firma} onChange={e => setFirma(e.target.value as FirmaId)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
              <option value="gutachter_koop">Gutachter Koop</option>
              <option value="lcs_gmbh">LCS GmbH</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Belegart</label>
            <select value={typ} onChange={e => setTyp(e.target.value as EingangsbelegTyp)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
              <option value="eingangsrechnung">Eingangsrechnung</option>
              <option value="quittung">Quittung</option>
              <option value="gutschrift">Gutschrift</option>
              <option value="sonstiges">Sonstiges</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Absender Firma</label>
              <input value={absenderFirma} onChange={e => setAbsenderFirma(e.target.value)}
                placeholder="z.B. ADAC, HUK-COBURG"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Absender Name</label>
              <input value={absenderName} onChange={e => setAbsenderName(e.target.value)}
                placeholder="Ansprechpartner"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Netto</label>
              <input type="number" step="0.01" value={netto} onChange={e => setNetto(e.target.value)}
                placeholder="0,00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">MwSt %</label>
              <input type="number" step="0.01" value={mwstSatz} onChange={e => setMwstSatz(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Brutto</label>
              <div className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 font-medium">
                {formatEuro(brutto)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Belegdatum</label>
              <input type="date" value={belegdatum} onChange={e => setBelegdatum(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Fällig am</label>
              <input type="date" value={faelligAm} onChange={e => setFaelligAm(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Verwendungszweck / Notiz</label>
            <input value={verwendungszweck} onChange={e => setVerwendungszweck(e.target.value)}
              placeholder="z.B. Rechnungs-Nr. 12345"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ALLE EINGÄNGE TAB
// ============================================================

const QUELLE_CONFIG: Record<InboxQuelle, { label: string; Icon: typeof Mail }> = {
  mail: { label: 'E-Mail', Icon: Mail },
  scan: { label: 'Scan', Icon: ScanLine },
  upload: { label: 'Upload', Icon: Upload },
  drive_sync: { label: 'Drive-Sync', Icon: RefreshCw },
  whatsapp: { label: 'WhatsApp', Icon: MessageSquare },
  fax: { label: 'Fax', Icon: Printer },
  manuell: { label: 'Manuell', Icon: PenLine },
};

const INBOX_STATUS_CONFIG: Record<InboxItemStatus, { label: string; color: string }> = {
  neu: { label: 'Neu', color: 'bg-blue-50 text-blue-600' },
  klassifiziert: { label: 'Klassifiziert', color: 'bg-amber-50 text-amber-600' },
  sortiert: { label: 'Sortiert', color: 'bg-emerald-50 text-emerald-600' },
  archiviert: { label: 'Archiviert', color: 'bg-gray-100 text-gray-500' },
  abgelehnt: { label: 'Abgelehnt', color: 'bg-red-50 text-red-600' },
};

const KI_KLASSE_LABELS: Partial<Record<InboxKlassifizierung, string>> = {
  eingangsrechnung: 'Eingangsrechnung',
  versicherungs_anschreiben: 'Vers. Anschreiben',
  versicherungs_abrechnung: 'Vers. Abrechnung',
  anwaltsschreiben: 'Anwaltsschreiben',
  gerichts_brief: 'Gerichtsbrief',
  schadensmeldung: 'Schadensmeldung',
  gutachten_anforderung: 'Gutachten-Anf.',
  reparatur_kostenvoranschlag: 'Kost.voranschlag',
  mahnung: 'Mahnung',
  zulassungsbescheinigung_zb1: 'ZB1',
  zulassungsbescheinigung_zb2: 'ZB2',
  kaufvertrag_fahrzeug: 'Kaufvertrag Kfz',
  leasingvertrag: 'Leasingvertrag',
  versicherungspolice: 'Vers.police',
  logo_branding: 'Logo/Branding',
  mitarbeiter_dokument: 'Mitarbeiterdok.',
  sonstiges: 'Sonstiges',
};

function InboxStatusBadge({ status }: { status: InboxItemStatus }) {
  const cfg = INBOX_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

function KlassifizierungBadge({ klasse }: { klasse: InboxKlassifizierung | null }) {
  if (!klasse) return null;
  return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-600">
      {KI_KLASSE_LABELS[klasse] || klasse}
    </span>
  );
}

function QuelleIcon({ quelle }: { quelle: InboxQuelle | null }) {
  if (!quelle) return <FileText className="w-3.5 h-3.5 text-gray-400" />;
  const cfg = QUELLE_CONFIG[quelle];
  if (!cfg) return <FileText className="w-3.5 h-3.5 text-gray-400" />;
  const Icon = cfg.Icon;
  return <Icon className="w-3.5 h-3.5 text-gray-400" aria-label={cfg.label} />;
}

// ── Sortier-Modal ────────────────────────────────────────────

const SORTIERBARE_ZIELMODULE = Object.keys(ZIELMODUL_LABELS) as Array<keyof typeof ZIELMODUL_LABELS>;

function SortierModal({
  item,
  onClose,
  onSortiert,
}: {
  item: InboxItem;
  onClose: () => void;
  onSortiert: (updatedItem: InboxItem) => void;
}) {
  const { profile } = useAuth();
  const [zielmodul, setZielmodul] = useState<string>(item.vorgeschlagene_zielmodul ?? 'buchhaltung');
  const [zielId, setZielId] = useState<string>(item.vorgeschlagene_ziel_id ?? '');
  const [saving, setSaving] = useState(false);

  const isBranding = zielmodul === 'branding_assets' || item.ki_klassifizierung === 'logo_branding';
  const needsZielId = !isBranding && !['buchhaltung', 'forderungsmanager', 'mitarbeiter', 'unbekannt'].includes(zielmodul);

  const handleBestaetigen = async () => {
    if (!profile?.id) {
      toast.error('Nicht eingeloggt');
      return;
    }
    setSaving(true);
    try {
      const updated = await sortInboxItem({
        id: item.id,
        zielmodul: isBranding ? 'branding_assets' : zielmodul,
        zielId: zielId.trim() || null,
        userId: profile.id,
      });
      toast.success('Dokument sortiert');
      onSortiert(updated);
    } catch (e: any) {
      toast.error('Fehler: ' + (e.message || 'Unbekannt'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="sortier-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Dokument sortieren</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Item-Preview */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-800 truncate">
              {item.betreff || item.absender_name || item.absender_email || '—'}
            </div>
            {item.ki_klassifizierung && (
              <div className="flex items-center gap-2 mt-1">
                <KlassifizierungBadge klasse={item.ki_klassifizierung} />
                {item.ki_konfidenz !== null && (
                  <span className="text-[10px] text-gray-400">KI-Konfidenz: {item.ki_konfidenz}%</span>
                )}
              </div>
            )}
          </div>

          {isBranding ? (
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 text-sm text-violet-700">
              <strong>Branding-Asset erkannt:</strong> Diese Datei wird automatisch in
              den Branding-Assets gespeichert und Andreas benachrichtigt.
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Zielmodul</label>
                <select
                  data-testid="sortier-modal-zielmodul"
                  value={zielmodul}
                  onChange={e => setZielmodul(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]"
                >
                  {SORTIERBARE_ZIELMODULE.map(m => (
                    <option key={m} value={m}>{ZIELMODUL_LABELS[m]}</option>
                  ))}
                </select>
              </div>

              {needsZielId && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Ziel-ID <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    data-testid="sortier-modal-ziel-id"
                    type="text"
                    value={zielId}
                    onChange={e => setZielId(e.target.value)}
                    placeholder="UUID des Ziel-Datensatzes"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A] font-mono text-xs"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            Abbrechen
          </button>
          <button
            data-testid="sortier-modal-bestaetigen"
            onClick={handleBestaetigen}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Sortieren bestätigen
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload Zone ──────────────────────────────────────────────

function UploadZone({ onUpload }: { onUpload: (file: File) => Promise<void> }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const err = validateUploadFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    setUploading(true);
    try {
      await onUpload(file);
      toast.success(`${file.name} hochgeladen`);
    } catch (e: any) {
      toast.error('Upload fehlgeschlagen: ' + (e.message || 'Unbekannt'));
    } finally {
      setUploading(false);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  return (
    <div
      data-testid="inbox-upload-zone"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors mb-4 ${
        dragOver ? 'border-[#C41E3A] bg-red-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 text-[#C41E3A] animate-spin" />
          <span className="text-sm text-gray-500">Wird hochgeladen…</span>
        </div>
      ) : (
        <>
          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">
            Dokument hier ablegen oder
          </p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-sm font-medium text-[#C41E3A] hover:underline"
          >
            Datei auswählen
          </button>
          <p className="text-[10px] text-gray-400 mt-1">Alle Dateitypen · max. 100 MB</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid="inbox-upload-input"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ── Inbox Item Row ───────────────────────────────────────────

function InboxItemRow({
  item,
  onSortieren,
  onAblehnen,
}: {
  item: InboxItem;
  onSortieren: (item: InboxItem) => void;
  onAblehnen: (item: InboxItem) => void;
}) {
  const canSort = item.status !== 'sortiert' && item.status !== 'archiviert';
  const canAblehnen = item.status !== 'abgelehnt' && item.status !== 'sortiert' && item.status !== 'archiviert';

  return (
    <tr
      key={item.id}
      data-testid={`inbox-item-${item.id}`}
      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
    >
      <td className="px-4 py-3 w-8">
        {item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt="" className="w-7 h-7 rounded object-cover" />
        ) : (
          <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center">
            <FileText className="w-3.5 h-3.5 text-gray-400" />
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-900 truncate max-w-[200px]">
          {item.betreff || item.absender_name || item.absender_email || '—'}
        </div>
        {item.absender_email && item.betreff && (
          <div className="text-[10px] text-gray-400 truncate">{item.absender_email}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <KlassifizierungBadge klasse={item.ki_klassifizierung} />
        {item.ki_konfidenz !== null && (
          <span className="text-[10px] text-gray-400 ml-1">{item.ki_konfidenz}%</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span data-testid={`inbox-item-status-${item.id}`}>
          <InboxStatusBadge status={item.status} />
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <QuelleIcon quelle={item.eingangs_quelle} />
          {item.eingangs_quelle && (
            <span className="text-[10px] text-gray-400">
              {QUELLE_CONFIG[item.eingangs_quelle]?.label ?? item.eingangs_quelle}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {formatDate(item.eingangs_datum)}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {canSort && (
            <button
              data-testid={`inbox-sortieren-btn-${item.id}`}
              onClick={() => onSortieren(item)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#C41E3A] bg-red-50 rounded hover:bg-red-100 transition-colors"
            >
              <ArrowRight className="w-3 h-3" />
              Sortieren
            </button>
          )}
          {canAblehnen && (
            <button
              data-testid={`inbox-ablehnen-btn-${item.id}`}
              onClick={() => onAblehnen(item)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-500 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              title="Nicht relevant"
            >
              <Ban className="w-3 h-3" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Alle Eingänge Tab ────────────────────────────────────────

function AlleEingaengeTab() {
  const [quelleFilter, setQuelleFilter] = useState<InboxQuelle | ''>('');
  const [klasseFilter, setKlasseFilter] = useState<InboxKlassifizierung | ''>('');
  const [statusFilter, setStatusFilter] = useState<InboxItemStatus | ''>('');
  const [konfidenzMin, setKonfidenzMin] = useState<number>(0);
  const [sortierItem, setSortierItem] = useState<InboxItem | null>(null);

  const { data: rawItems, loading, error, refetch } = useInboxItems({
    eingangs_quelle: quelleFilter || undefined,
    ki_klassifizierung: klasseFilter || undefined,
    status: statusFilter || undefined,
  });

  const [localItems, setLocalItems] = useState<InboxItem[]>([]);
  const items = useMemo(
    () => filterInboxItems(localItems.length > 0 ? localItems : rawItems, { ki_konfidenz_min: konfidenzMin }),
    [rawItems, localItems, konfidenzMin],
  );

  // Keep localItems in sync when rawItems changes
  useEffect(() => { setLocalItems(rawItems); }, [rawItems]);

  const handleUpload = useCallback(async (file: File) => {
    await uploadInboxItem(file);
    refetch();
  }, [refetch]);

  const handleSortiert = useCallback((updatedItem: InboxItem) => {
    setLocalItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
    setSortierItem(null);
  }, []);

  const handleAblehnen = useCallback(async (item: InboxItem) => {
    try {
      const updated = await ablehnenInboxItem(item.id);
      setLocalItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success('Als „Nicht relevant" markiert');
    } catch (e: any) {
      toast.error('Fehler: ' + (e.message || 'Unbekannt'));
    }
  }, []);

  return (
    <div>
      {sortierItem && (
        <SortierModal
          item={sortierItem}
          onClose={() => setSortierItem(null)}
          onSortiert={handleSortiert}
        />
      )}

      <UploadZone onUpload={handleUpload} />

      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <select
          data-testid="inbox-filter-quelle"
          value={quelleFilter}
          onChange={e => setQuelleFilter(e.target.value as InboxQuelle | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
        >
          <option value="">Alle Quellen</option>
          {(Object.keys(QUELLE_CONFIG) as InboxQuelle[]).map(q => (
            <option key={q} value={q}>{QUELLE_CONFIG[q].label}</option>
          ))}
        </select>

        <select
          data-testid="inbox-filter-klassifizierung"
          value={klasseFilter}
          onChange={e => setKlasseFilter(e.target.value as InboxKlassifizierung | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
        >
          <option value="">Alle Klassifizierungen</option>
          {(Object.keys(KI_KLASSE_LABELS) as InboxKlassifizierung[]).map(k => (
            <option key={k} value={k}>{KI_KLASSE_LABELS[k]}</option>
          ))}
        </select>

        <select
          data-testid="inbox-filter-status"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as InboxItemStatus | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
        >
          <option value="">Alle Status</option>
          {(Object.keys(INBOX_STATUS_CONFIG) as InboxItemStatus[]).map(s => (
            <option key={s} value={s}>{INBOX_STATUS_CONFIG[s].label}</option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">KI-Konfidenz ≥</label>
          <input
            data-testid="inbox-filter-konfidenz"
            type="number"
            min={0}
            max={100}
            step={10}
            value={konfidenzMin}
            onChange={e => setKonfidenzMin(Number(e.target.value))}
            className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
          />
          <span className="text-xs text-gray-400">%</span>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-sm text-red-600 py-4">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">Keine Eingänge gefunden</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 w-8" />
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Betreff / Absender</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">KI-Klasse</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Quelle</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Datum</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <InboxItemRow
                      key={item.id}
                      item={item}
                      onSortieren={setSortierItem}
                      onAblehnen={handleAblehnen}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Buchhaltungs-Belege Tab (bisherige Ansicht) ──────────────

function BuchhaltungsBelegeTab() {
  const [firmaFilter, setFirmaFilter] = useState<FirmaId | ''>('');
  const [statusFilter, setStatusFilter] = useState<EingangsbelegStatus | ''>('');
  const [search, setSearch] = useState('');
  const [showErfassen, setShowErfassen] = useState(false);

  const { data: belegeRaw, loading, refetch } = useEingangsbelege(
    firmaFilter || undefined,
    statusFilter || undefined,
  );
  const belege = belegeRaw || [];

  const filtered = useMemo(() => {
    if (!search.trim()) return belege;
    const q = search.toLowerCase();
    return belege.filter(b =>
      (b.absender_firma || '').toLowerCase().includes(q) ||
      (b.absender_name || '').toLowerCase().includes(q) ||
      (b.beleg_nr || '').toLowerCase().includes(q) ||
      (b.verwendungszweck || '').toLowerCase().includes(q)
    );
  }, [belege, search]);

  const kpis = useMemo(() => {
    const neue = belege.filter(b => b.status === 'neu');
    const geprueft = belege.filter(b => b.status === 'geprueft');
    const zurFreigabe = belege.filter(b => b.status === 'gebucht');
    const offen = belege.filter(b => !['ueberwiesen', 'abgelehnt'].includes(b.status));
    const offenBetrag = offen.reduce((s, b) => s + (b.brutto_betrag || 0), 0);
    return { neue: neue.length, geprueft: geprueft.length, zurFreigabe: zurFreigabe.length, offenBetrag };
  }, [belege]);

  const handleCreate = async (beleg: Partial<Eingangsbeleg>) => {
    try {
      await createEingangsbeleg(beleg);
      toast.success('Beleg erfasst');
      setShowErfassen(false);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    }
  };

  const handleStatusChange = async (id: string, newStatus: EingangsbelegStatus) => {
    try {
      await updateEingangsbeleg(id, { status: newStatus });
      toast.success(`Status auf "${STATUS_CONFIG[newStatus]?.label || newStatus}" gesetzt`);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="buchhaltung-belege-content">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowErfassen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors min-h-[44px]">
          <Plus className="w-4 h-4" />
          Beleg erfassen
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-medium text-blue-900">E-Mail-Integration</div>
          <div className="text-xs text-blue-700 mt-0.5">
            Rechnungen an <span className="font-mono font-semibold">rechnung@gutachter-koop.de</span> werden automatisch
            im Eingangskorb erfasst und per KI vorklassifiziert.
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <KPICard label="Neue Belege" value={String(kpis.neue)} icon={Inbox} color="#2563EB" />
        <KPICard label="Geprüft" value={String(kpis.geprueft)} icon={Eye} color="#D97706" />
        <KPICard label="Zur Freigabe" value={String(kpis.zurFreigabe)} icon={CheckCircle2} color="#059669" />
        <KPICard label="Offener Betrag" value={formatEuro(kpis.offenBetrag)} icon={Euro} color="#DC2626" />
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Absender, Beleg-Nr, Verwendungszweck..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
        </div>
        <select value={firmaFilter} onChange={e => setFirmaFilter(e.target.value as FirmaId | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20">
          <option value="">Alle Firmen</option>
          <option value="gutachter_koop">Gutachter Koop</option>
          <option value="lcs_gmbh">LCS GmbH</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as EingangsbelegStatus | '')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20">
          <option value="">Alle Status</option>
          <option value="neu">Neu</option>
          <option value="geprueft">Geprüft</option>
          <option value="gebucht">Gebucht</option>
          <option value="freigegeben">Freigegeben</option>
          <option value="ueberwiesen">Überwiesen</option>
          <option value="abgelehnt">Abgelehnt</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div className="text-sm">Keine Belege gefunden</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Beleg</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Absender</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Firma</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Betrag</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Datum</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">{b.beleg_nr || '—'}</div>
                          <div className="text-[10px] text-gray-400">{TYP_LABELS[b.typ] || b.typ}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{b.absender_firma || b.absender_name || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{FIRMA_LABELS[b.firma] || b.firma}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-gray-900">{formatEuro(b.brutto_betrag)}</div>
                      <div className="text-[10px] text-gray-400">netto {formatEuro(b.netto_betrag)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-600">{formatDate(b.belegdatum || b.eingangsdatum)}</div>
                      {b.faellig_am && (
                        <div className="text-[10px] text-gray-400">fällig {formatDate(b.faellig_am)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={b.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {b.status === 'neu' && (
                          <button onClick={() => handleStatusChange(b.id, 'geprueft')}
                            className="px-2 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                            Prüfen
                          </button>
                        )}
                        {b.status === 'geprueft' && (
                          <button onClick={() => handleStatusChange(b.id, 'freigegeben')}
                            className="px-2 py-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors">
                            Freigeben
                          </button>
                        )}
                        {b.status === 'freigegeben' && (
                          <button onClick={() => handleStatusChange(b.id, 'ueberwiesen')}
                            className="px-2 py-1 text-[10px] font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors">
                            Überweisen
                          </button>
                        )}
                        {!['ueberwiesen', 'abgelehnt'].includes(b.status) && (
                          <button onClick={() => handleStatusChange(b.id, 'abgelehnt')}
                            className="px-2 py-1 text-[10px] font-medium text-red-500 bg-red-50 rounded hover:bg-red-100 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ErfassenDialog open={showErfassen} onClose={() => setShowErfassen(false)} onSave={handleCreate} />
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function EingangskorbPage() {
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Eingangskorb</h1>
        <p className="text-sm text-gray-500 mt-1">Alle eingehenden Dokumente auf einen Blick</p>
      </div>

      <Tabs defaultValue="alle">
        <TabsList className="mb-6">
          <TabsTrigger value="alle" data-testid="eingangskorb-tab-alle">
            Alle Eingänge
          </TabsTrigger>
          <TabsTrigger value="buchhaltung" data-testid="eingangskorb-tab-buchhaltung">
            Buchhaltungs-Belege
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alle">
          <AlleEingaengeTab />
        </TabsContent>

        <TabsContent value="buchhaltung">
          <BuchhaltungsBelegeTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
